import { ClientDataGrid } from '@cmg/data-grid';
import { Button, Stack, styled, Typography } from '@cmg/design-system';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { useParams } from 'react-router-dom';

import { FilteredGridNoRowOverlay } from '../../shared/components/filtered-grid-no-row-overlay/FilteredGridNoRowOverlay';
import { DeleteParticipationSetDialog } from './DeleteParticipationSetDialog';
import { OfferingProfile_GetParticipationRecordsQuery } from './graphql/__generated__/GetParticipationRecords';
import { useCreateColumnDefs } from './hooks/useCreateColumnDefs';
import { useImportSetRouteViewModel } from './hooks/useImportSetRouteViewModel';
import { useSideBarDef } from './hooks/useSideBarDef';
import { ParticipationRecordItem } from './types';
import { UploadParticipationSetModal } from './UploadParticipationSetModal';

const StyledClientDataGrid = styled(ClientDataGrid<ParticipationRecordItem>)`
  margin: ${({ theme }) => `0 ${theme.spacing(2)}`};
  .ag-overlay-wrapper {
    align-items: flex-start;
    margin-top: ${({ theme }) => theme.spacing(3.75)};
  }
  .upload-button {
    pointer-events: all;
  }
`;

export type Props = {
  issuerName: string;
  participation?: OfferingProfile_GetParticipationRecordsQuery;
  refetchParticipationQuery: () => void;
  isDeleteDialogOpen: boolean;
  onCloseDeleteDialog: () => void;
  loading: boolean;
  isImportDialogOpen: boolean;
  onOpenImportDialog: () => void;
  onCloseImportDialog: () => void;
};
export const ViewParticipationSet: React.FC<Props> = ({
  issuerName,
  refetchParticipationQuery,
  participation,
  isDeleteDialogOpen,
  onCloseDeleteDialog,
  loading,
  isImportDialogOpen,
  onOpenImportDialog,
  onCloseImportDialog,
}) => {
  const columnDefs = useCreateColumnDefs();
  const { offeringId } = useParams<{ offeringId: string }>();
  const sideBarDef = useSideBarDef();

  const { handleReset, handleFileInputChange, uploadSet, selectedFile, error } =
    useImportSetRouteViewModel({
      offeringId,
      onCompleted: () => {
        handleReset();
        onCloseImportDialog();
        refetchParticipationQuery();
      },
    });

  const noRowsOverlayComponentParams: NoRowOverlayProps = React.useMemo(() => {
    return {
      handleOpenModal: () => {
        handleReset();
        onOpenImportDialog();
      },
      hasUploadedData: !isEmpty(participation?.participationRecords?.items),
    };
  }, [handleReset, onOpenImportDialog, participation?.participationRecords?.items]);

  const totalRow = React.useMemo(() => {
    const result = {
      investorName: `Total: ${participation?.participationRecords?.items?.length ?? 0}`,
    };
    participation?.participationRecords?.items?.forEach(row => {
      for (const key in row) {
        if (row.hasOwnProperty(key) && typeof row[key] === 'number') {
          result[key] = (result[key] ?? 0) + row[key];
        }
      }
    });
    return result;
  }, [participation?.participationRecords?.items]);
  return (
    <React.Fragment>
      <StyledClientDataGrid
        rowData={participation?.participationRecords?.items ?? []}
        columnDefs={columnDefs}
        loading={loading}
        pinnedTopRowData={[totalRow]}
        noRowsOverlayComponent={NoRowOverlay}
        noRowsOverlayComponentParams={noRowsOverlayComponentParams}
        sideBar={sideBarDef}
      />
      <UploadParticipationSetModal
        isOpen={isImportDialogOpen}
        onClose={() => {
          handleReset();
          onCloseImportDialog();
        }}
        onFileSelected={handleFileInputChange}
        onUpload={uploadSet}
        selectedFile={selectedFile}
        error={error}
        issuerName={issuerName}
        participation={participation}
      />
      <DeleteParticipationSetDialog
        isOpen={isDeleteDialogOpen}
        onClose={onCloseDeleteDialog}
        offeringId={offeringId}
      />
    </React.Fragment>
  );
};
type NoRowOverlayProps = {
  handleOpenModal: () => void;
  hasUploadedData: boolean;
};

const NoRowOverlay: React.FC<NoRowOverlayProps> = ({ handleOpenModal, hasUploadedData }) => {
  return !hasUploadedData ? (
    <Stack alignItems="center" spacing={1} marginTop={4}>
      <Typography variant="h3">Upload Data</Typography>
      <Typography color="text.secondary">
        Upload your private participation data to view Investor level participation analysis
      </Typography>
      <Button className="upload-button" size="small" variant="contained" onClick={handleOpenModal}>
        Upload Participation
      </Button>
    </Stack>
  ) : (
    <FilteredGridNoRowOverlay />
  );
};
